/**
 * Sewing Efficiency — the bar-and-target chart on the summary view.
 *
 * One chart for the lines in scope: every line at the factory until a Building-Floor is chosen,
 * then that floor alone. The same component and the same series, so whole-factory and one-floor
 * cannot disagree about what they are plotting.
 *
 * Two server-computed numbers per line, chosen by the Output/Efficiency mode in
 * {@link plottedFields}. Efficiency arrives as a 0–1 fraction and is scaled by 100 for the axis.
 *
 * Grain `floor` rolls those lines to Building–Floor bars in the browser (chart only — table stays
 * line grain; CLAUDE.md §4.3 / DECISIONS.md §31). Floor efficiency is SUM(earned)/SUM(worked),
 * never the mean of line efficiencies.
 *
 * The bar colour follows the comparison of those two numbers — presentation, inventing no
 * threshold. A line (or floor) the data gives no target for is drawn neutral, never counted as a
 * failure.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTrendDown, faArrowTrendUp } from '@fortawesome/free-solid-svg-icons';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  usePlotArea,
  useXAxisScale,
} from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartCells,
  chartColor,
  type ChartColorToken,
  type ChartConfig,
} from '@/components/ui/chart';
import { decimal, integer } from '@/components/report/groupedColumns';
import { cn } from '@/lib/utils';

import {
  efficiencyField,
  plottedFields,
  TARGET_EFFICIENCY_FIELD,
  type PlottedFields,
  type SewingRow,
} from './columns';
import type { ChartGrain, ChartMode, Metric } from './filters';
import { rollupFloorBars } from './floor-rollup';
import { STANDING, StandingArrow, standing } from './target';

/** Past this many bars the tick labels overlap, so Recharts thins them. Also the marker crossover. */
const LABEL_EVERY_BAR_UNTIL = 45;

/**
 * The target marker's size, as the **area in px²** a `<ZAxis range>` carries: 64 draws 9.0px and 28
 * draws 6.0px. Two sizes, because at factory-wide density a 9px dot leaves 3px to its neighbour and
 * the row of targets reads as one dotted band.
 */
const TARGET_MARK_AREA = { roomy: 64, tight: 28 } as const;

interface Datum {
  /** Line code, or Building–Floor spotlight key when grain is floor. */
  id: string;
  /** Axis label. */
  label: string;
  lineType?: string;
  actual: number | null;
  target: number | null;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Fractions on the wire, percentages on the axis; piece counts pass through untouched. */
function scaled(value: unknown, isPercent: boolean): number | null {
  const parsed = toNumber(value);
  if (parsed === null) return null;
  return isPercent ? parsed * 100 : parsed;
}

/**
 * The bar's fill: green at or over target, red under it, blue where there is no target. Green
 * against red is affordable here only because colour is not the only channel (§4.9) — each bar
 * carries its target as a scatter dot at the target's own height, and the caption says all three
 * in words.
 */
function barToken(datum: Datum): ChartColorToken {
  if (datum.actual === null || datum.target === null || datum.target <= 0) {
    // Makes no claim: there is no second number. Common at sites with patchy target coverage.
    return 'chart-1';
  }
  return datum.actual < datum.target ? 'status-critical' : 'status-ok';
}

/**
 * The same fill for the tooltip's swatch: `config` carries one colour per *series*, so without this
 * hovering a red bar shows a blue dot. Re-runs {@link barToken} rather than restating it. The datum
 * arrives as `unknown` (Recharts types the row as `any`) and is read field-by-field, not cast.
 */
function tooltipBarToken(seriesKey: string | undefined, datum: unknown): ChartColorToken | undefined {
  if (seriesKey !== 'actual' || typeof datum !== 'object' || datum === null) {
    return undefined;
  }
  const row = datum as Record<string, unknown>;
  return barToken({
    id: '',
    label: '',
    actual: toNumber(row['actual']),
    target: toNumber(row['target']),
  });
}

function toLineData(rows: readonly SewingRow[], fields: PlottedFields): Datum[] {
  return rows
    .map((row, index) => ({
      id: String(row['line_no'] ?? index),
      label: String(row['show_line_no'] ?? row['line_no'] ?? index),
      lineType: String(row['line_type_name'] ?? 'Conventional'),
      actual: scaled(row[fields.actual], fields.isPercent),
      target: scaled(row[fields.target], fields.isPercent),
    }))
    .sort((a, b) => a.lineType.localeCompare(b.lineType) || a.label.localeCompare(b.label));
}

function LineTypeDividers({
  groups,
  data,
}: {
  groups: readonly { label: string; count: number }[];
  data: readonly Datum[];
}) {
  const plot = usePlotArea();
  const xScale = useXAxisScale();
  if (!plot || !xScale || data.length === 0) return null;

  return (
    <g aria-hidden="true" pointerEvents="none">
      {groups.slice(0, -1).map((group, index) => {
        const boundary = groups.slice(0, index + 1).reduce((sum, each) => sum + each.count, 0);
        const before = data[boundary - 1];
        const after = data[boundary];
        const beforeCenter = xScale?.(boundary - 1, { position: 'middle' })
          ?? xScale?.(before?.label, { position: 'middle' });
        const afterCenter = xScale?.(boundary, { position: 'middle' })
          ?? xScale?.(after?.label, { position: 'middle' });
        if (typeof beforeCenter !== 'number' || !Number.isFinite(beforeCenter)
            || typeof afterCenter !== 'number' || !Number.isFinite(afterCenter)) return null;
        const x = (beforeCenter + afterCenter) / 2;
        return (
          <line
            key={group.label}
            x1={x}
            x2={x}
            y1={0}
            y2={plot.y + plot.height + 72}
            className="stroke-muted-foreground"
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        );
      })}
    </g>
  );
}

function toFloorData(
  rows: readonly SewingRow[],
  mode: ChartMode,
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): Datum[] {
  // Rollup already scales efficiency to percent points.
  return rollupFloorBars(rows, mode, metric, includePpa, available).map((bar) => ({
    id: bar.key,
    label: bar.label,
    actual: bar.actual,
    target: bar.target,
  }));
}

function chartTitle(fields: PlottedFields, grain: ChartGrain): string {
  if (grain === 'floor') {
    return fields.isPercent ? 'Sewing floor efficiency (%)' : 'Sewing floor output (pcs)';
  }
  return fields.title;
}

function formatValue(value: number, isPercent: boolean): string {
  return isPercent ? `${decimal.format(value)}%` : integer.format(Math.round(value));
}

export interface EfficiencyChartProps {
  rows: readonly SewingRow[];
  mode: ChartMode;
  metric: Metric;
  includePpa: boolean;
  /** Fields the response actually carries, so the chart reads the same columns the table shows. */
  available: ReadonlySet<string>;
  /** Line (default) or Building–Floor rollup. Floor is chart-only; table stays line grain. */
  grain?: ChartGrain;
  /** Names what this chart covers — "every line at BCA", "every floor at BVN". Accessible name. */
  scope: string;
  /** Tailwind height class. Factory-wide scope is taller because it carries more bars. */
  heightClass?: string;
  /** Opens that line's daily trend under the chart (or whatever the page wires here). */
  onSelectLine?: (lineNo: string) => void;
  /** Selects that Building–Floor in the spotlight (floor grain only). */
  onSelectFloor?: (buildingFloorValue: string) => void;
  /** What a bar click does, in words, so the interaction is discoverable without hovering first. */
  selectHint?: string;
  emptyMessage?: string;
}

export function EfficiencyChart({
  rows,
  mode,
  metric,
  includePpa,
  available,
  grain = 'line',
  scope,
  heightClass = 'h-64',
  onSelectLine,
  onSelectFloor,
  selectHint,
  emptyMessage = 'No lines ran in this period. Widen the dates, or clear the building and brand.',
}: EfficiencyChartProps) {
  const fields = plottedFields(mode, metric, includePpa, available);
  const title = chartTitle(fields, grain);
  const data =
    grain === 'floor'
      ? toFloorData(rows, mode, metric, includePpa, available)
      : toLineData(rows, fields);
  const typeGroups: { label: string; count: number }[] = [];
  if (grain === 'line') {
    for (const datum of data) {
      const label = datum.lineType ?? 'Conventional';
      const previous = typeGroups.at(-1);
      if (previous?.label === label) previous.count += 1;
      else typeGroups.push({ label, count: 1 });
    }
  }
  const showTypeGroups = typeGroups.length > 1;
  const markArea =
    data.length > LABEL_EVERY_BAR_UNTIL ? TARGET_MARK_AREA.tight : TARGET_MARK_AREA.roomy;

  const onSelect =
    grain === 'floor'
      ? onSelectFloor
      : onSelectLine;
  const resolvedHint =
    selectHint ??
    (grain === 'floor'
      ? 'Select a bar to show that floor\'s lines.'
      : 'Select a bar to show that line day by day below.');
  const unitNoun = grain === 'floor' ? 'floor' : 'line';
  const grainNoun = grain === 'floor' ? 'building and floor' : 'sewing line';

  const config = {
    actual: { label: fields.actualLabel, color: 'chart-1' },
    target: { label: fields.targetLabel, color: 'foreground' },
  } satisfies ChartConfig;

  return (
    <section className="flex flex-col gap-1.5" aria-labelledby={`chart-${scope}`}>
      <h2 id={`chart-${scope}`} className="text-sm font-medium">
        {title} — {scope}
      </h2>

      {data.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div className="relative">
        {showTypeGroups && (
          <div
            className="flex"
            style={{ paddingLeft: fields.isPercent ? 53 : 65, paddingRight: 8 }}
            role="list"
            aria-label="Line type groups from left to right"
          >
            {typeGroups.map((group) => (
              <div
                key={group.label}
                className="min-w-0 py-1 text-center text-xs font-medium text-foreground"
                style={{ flexGrow: group.count, flexBasis: 0 }}
                role="listitem"
                title={group.label}
              >
                <span className="block truncate px-1">{group.label}</span>
              </div>
            ))}
          </div>
        )}
        <ChartContainer
          config={config}
          aria-label={`${title} against target by ${grainNoun}, ${scope}`}
          className={cn('aspect-auto w-full', heightClass)}
        >
          <ComposedChart data={data} accessibilityLayer margin={{ top: 8, right: 8, bottom: 0 }}>
            <CartesianGrid vertical={false} />
            {showTypeGroups && <LineTypeDividers groups={typeGroups} data={data} />}
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              // Rotated: a factory can put 150 lines on this axis.
              interval={data.length > LABEL_EVERY_BAR_UNTIL ? 'preserveStartEnd' : 0}
              angle={-45}
              textAnchor="end"
              height={72}
            />
            <YAxis
              width={fields.isPercent ? 48 : 60}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) =>
                fields.isPercent
                  ? `${String(Math.round(value))}%`
                  : integer.format(Math.round(value))
              }
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  hiddenSeries={['label']}
                  seriesOrder={['target', 'actual']}
                  // So the swatch is the colour of the bar under the pointer, not the series colour.
                  seriesColor={tooltipBarToken}
                  formatter={(value) =>
                    typeof value === 'number' ? formatValue(value, fields.isPercent) : String(value)
                  }
                />
              }
            />
            <Bar
              dataKey="actual"
              radius={2}
              className={onSelect === undefined ? undefined : 'cursor-pointer'}
              // A Recharts presentation attribute reading a theme token, not the inline `style`
              // §4.8 rules out — same route `<Scatter fill>` below takes.
              activeBar={
                onSelect === undefined
                  ? false
                  : { stroke: chartColor('foreground'), strokeWidth: 1.5 }
              }
              onClick={(_bar, index: number) => {
                const datum = data[index];
                if (datum !== undefined) {
                  onSelect?.(datum.id);
                }
              }}
            >
              {chartCells(data, barToken)}
            </Bar>
            {/* Renders nothing: `range` is the only way Recharts takes a scatter symbol's size. */}
            <ZAxis type="number" range={[markArea, markArea]} />
            {/* A marker, not a line: joining targets would draw a trend across unordered
                categories. A circle, to match the legend swatch. `name` is set explicitly because
                Recharts fills it from `dataKey` for a `Bar` but not a `Scatter`, and the shared
                tooltip only formats an entry that has both a value and a name. */}
            <Scatter
              dataKey="target"
              name="target"
              shape="circle"
              fill={chartColor('foreground')}
            />
          </ComposedChart>
        </ChartContainer>
        </div>
      )}

      {/* Colour is never the only carrier of meaning (§4.9). Not a status vocabulary (§4.6): it
          names one comparison, of a bar against its own target (line or floor rollup). */}
      <p className="text-xs text-muted-foreground">
        Bars are green where a {unitNoun} reached its own target and red where it fell short,
        against the marker on each bar. Blue means the data carries no target for that {unitNoun} to
        compare against.
        {onSelect === undefined ? null : ` ${resolvedHint}`}
      </p>
    </section>
  );
}

interface Ranked {
  lineNo: string;
  line: string;
  efficiency: number;
  /** Read from the row, not derived: `target_efficiency` as the server computed it. */
  target: number | null;
}

export interface TopBottomProps {
  /** Lines currently on the chart — whole factory or one floor — from a complete result. */
  rows: readonly SewingRow[];
  metric: Metric;
  includePpa: boolean;
  available: ReadonlySet<string>;
  onSelectLine?: (lineNo: string) => void;
}

/** How many lines each panel names. Legacy showed three; so does this. */
const RANK_SIZE = 3;

/**
 * The best and worst three lines in the chart's scope, from the same rows the bars draw. Ordering,
 * not calculation: values are the server's `*_efficiency` read as-is, and a line whose efficiency
 * came back null is left out of both panels rather than ranked worst — absent is not low.
 */
export function TopBottomLines({
  rows,
  metric,
  includePpa,
  available,
  onSelectLine,
}: TopBottomProps) {
  const field = efficiencyField(metric, includePpa, available);
  const targetField = TARGET_EFFICIENCY_FIELD;

  const ranked: Ranked[] = rows
    .flatMap((row) => {
      const efficiency = toNumber(row[field]);
      return efficiency === null
        ? []
        : [
            {
              lineNo: String(row['line_no']),
              line: String(row['show_line_no'] ?? row['line_no']),
              efficiency,
              target: toNumber(row[targetField]),
            },
          ];
    })
    .sort((a, b) => b.efficiency - a.efficiency);

  if (ranked.length === 0) {
    return null;
  }

  const top = ranked.slice(0, RANK_SIZE);
  // Worst first, so the line that most needs attention is the first thing read (§4.6).
  const last = ranked.slice(-RANK_SIZE).reverse();

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <RankPanel
        title={`Top ${String(top.length)} by efficiency`}
        icon={faArrowTrendUp}
        lines={top}
        onSelectLine={onSelectLine}
      />
      <RankPanel
        title={`Last ${String(last.length)} by efficiency`}
        icon={faArrowTrendDown}
        lines={last}
        onSelectLine={onSelectLine}
      />
    </div>
  );
}

/**
 * One panel of three lines, coloured by the same comparison the cards and bars use. A wash behind
 * the row rather than colour on the figure: `status-ok` does not clear 4.5:1 for 14px text, and a
 * wash is not text. The arrow and the row's accessible name carry the same fact without colour.
 */
function RankPanel({
  title,
  icon,
  lines,
  onSelectLine,
}: {
  title: string;
  icon: typeof faArrowTrendUp;
  lines: readonly Ranked[];
  onSelectLine?: (lineNo: string) => void;
}) {
  return (
    <section aria-label={title} className="rounded-lg border bg-card p-2">
      <h3 className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <FontAwesomeIcon icon={icon} className="size-3" aria-hidden />
        {title}
      </h3>
      <ol className="flex flex-col gap-0.5">
        {lines.map((line, index) => {
          const of = standing(line.efficiency, line.target);
          const against =
            line.target === null ? 'no target' : `vs ${decimal.format(line.target * 100)}%`;

          return (
            <li
              key={line.lineNo}
              className={cn(
                'flex items-baseline gap-2 rounded-sm px-1.5 py-0.5 text-sm',
                of === null ? undefined : STANDING[of].wash,
              )}
            >
              <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">
                {index + 1}
              </span>
              {onSelectLine === undefined ? (
                <span className="min-w-0 flex-1 truncate">{line.line}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onSelectLine(line.lineNo);
                  }}
                  className="min-w-0 flex-1 truncate rounded-sm text-left underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                  aria-label={`Show ${line.line} day by day`}
                >
                  {line.line}
                </button>
              )}
              {of === null ? null : (
                <>
                  <StandingArrow of={of} className="size-3 self-center" />
                  {/* What a screen reader, a greyscale print and a colour-blind reader get. */}
                  <span className="sr-only">{STANDING[of].words}</span>
                </>
              )}
              <span className="shrink-0 font-medium tabular-nums">
                {decimal.format(line.efficiency * 100)}%
              </span>
              {/* Every figure with a target shows it (§4.6). */}
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{against}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
